// ─── Game state + reducer ───────────────────────────────────────────────────
// Ported from src/store/gameStore.ts (Daily Challenge path only — no Remix, no
// ad-continue). The scoring, chain, combo, queue-refill and game-over rules are
// kept byte-faithful to the native store. Pure game modules are imported verbatim.

import {
  ALL_COLORS,
  LANE_COUNT,
  ROW_COUNT,
  type Chain,
  type Color,
  type GridCell,
  type MoveRecord,
} from '@gametypes';
import { detectChains } from '@game/chainDetector';
import { mulberry32, buildOnboardingQueue, ONBOARDING_SEED } from '@game/seed';

export type Grid = GridCell[][];
export type Status = 'idle' | 'playing' | 'paused' | 'gameover';

const QUEUE_REFILL_THRESHOLD = 10;
const QUEUE_REFILL_BATCH = 40;
const MAX_COMBO = 8;

function emptyGrid(): Grid {
  return Array.from({ length: LANE_COUNT }, () =>
    Array.from({ length: ROW_COUNT }, (): GridCell => null),
  );
}

/** buildQueue is a local helper in gameStore.ts — replicated, not forked. */
function buildQueue(rng: () => number, length = 20): Color[] {
  return Array.from({ length }, () => ALL_COLORS[Math.floor(rng() * ALL_COLORS.length)]);
}

function lowestEmptyRow(grid: Grid, laneIndex: number): number {
  for (let row = 0; row < ROW_COUNT; row++) {
    if (grid[laneIndex][row] === null) return row;
  }
  return -1;
}

function hasSpace(grid: Grid): boolean {
  return grid.some((lane) => lane.some((cell) => cell === null));
}

export interface DropResult {
  landed: boolean;
  chains: Chain[];
  scoreGained: number;
  wasOnBeat: boolean;
  comboAfter: number;
  gameOver: boolean;
  /** true for the first chain of an onboarding run (drives onboarding_chain_fired) */
  firstOnboardingChain: boolean;
}

export class GameEngine {
  grid: Grid = emptyGrid();
  queue: Color[] = [];
  score = 0;
  combo = 1;
  maxCombo = 1;
  chainsCleared = 0;
  onBeatCount = 0;
  totalMoves = 0;
  status: Status = 'idle';
  moves: MoveRecord[] = [];
  startedAt: number | null = null;
  isOnboarding = false;

  private rng: (() => number) | null = null;
  private sawChain = false;

  startGame(seed: string): void {
    this.rng = mulberry32(seed);
    this.isOnboarding = seed === ONBOARDING_SEED;
    this.queue = this.isOnboarding
      ? buildOnboardingQueue(this.rng)
      : buildQueue(this.rng, 40);
    this.grid = emptyGrid();
    this.score = 0;
    this.combo = 1;
    this.maxCombo = 1;
    this.chainsCleared = 0;
    this.onBeatCount = 0;
    this.totalMoves = 0;
    this.moves = [];
    this.startedAt = Date.now();
    this.status = 'playing';
    this.sawChain = false;
  }

  pause(): void {
    if (this.status === 'playing') this.status = 'paused';
  }

  resume(): void {
    if (this.status === 'paused') this.status = 'playing';
  }

  reset(): void {
    this.rng = null;
    this.grid = emptyGrid();
    this.queue = [];
    this.score = 0;
    this.combo = 1;
    this.maxCombo = 1;
    this.chainsCleared = 0;
    this.onBeatCount = 0;
    this.totalMoves = 0;
    this.moves = [];
    this.startedAt = null;
    this.status = 'idle';
    this.sawChain = false;
  }

  /** The colour currently falling (queue head). */
  get currentColor(): Color | undefined {
    return this.queue[0];
  }

  /** Landing row for a lane (first empty from the bottom), or -1 if full. */
  landingRow(laneIndex: number): number {
    return lowestEmptyRow(this.grid, laneIndex);
  }

  /**
   * Place the current token into `laneIndex`. Mirrors gameStore.dropItem.
   * Returns what happened so the renderer / audio / analytics layers can react.
   */
  dropItem(laneIndex: number, wasOnBeat: boolean): DropResult {
    const empty: DropResult = {
      landed: false,
      chains: [],
      scoreGained: 0,
      wasOnBeat,
      comboAfter: this.combo,
      gameOver: false,
      firstOnboardingChain: false,
    };
    if (this.status !== 'playing') return empty;

    const color = this.queue[0];
    if (!color) return empty;

    const row = lowestEmptyRow(this.grid, laneIndex);
    if (row === -1) {
      if (!hasSpace(this.grid)) {
        this.status = 'gameover';
        return { ...empty, gameOver: true };
      }
      return empty;
    }

    // place
    this.grid[laneIndex][row] = color;

    const elapsed = this.startedAt ? Date.now() - this.startedAt : 0;
    this.moves.push({ laneIndex, timestamp: elapsed, color });

    const chains = detectChains(this.grid as GridCell[][]);
    let scoreGained = 0;

    if (chains.length > 0) {
      for (const chain of chains) {
        const multiplier = wasOnBeat ? 1.5 : 1;
        const pts = Math.floor(chain.length * 10 * this.combo * multiplier);
        scoreGained += pts;
        this.chainsCleared += 1;
        if (wasOnBeat) this.onBeatCount += 1;
      }
      this.score += scoreGained;
      this.combo = Math.min(this.combo + chains.length, MAX_COMBO);
      this.maxCombo = Math.max(this.maxCombo, this.combo);

      // clear chained cells
      for (const c of chains) {
        for (let r = c.startRow; r < c.startRow + c.length; r++) {
          this.grid[c.laneIndex][r] = null;
        }
      }
    } else {
      this.combo = 1;
    }

    // pop + refill
    this.queue = this.queue.slice(1);
    if (this.rng && this.queue.length < QUEUE_REFILL_THRESHOLD) {
      this.queue = [...this.queue, ...buildQueue(this.rng, QUEUE_REFILL_BATCH)];
    }

    this.totalMoves += 1;

    const firstOnboardingChain =
      this.isOnboarding && chains.length > 0 && !this.sawChain;
    if (chains.length > 0) this.sawChain = true;

    const gameOver = !hasSpace(this.grid);
    if (gameOver) this.status = 'gameover';

    return {
      landed: true,
      chains,
      scoreGained,
      wasOnBeat,
      comboAfter: this.combo,
      gameOver,
      firstOnboardingChain,
    };
  }
}
